/* ==========================================================================
 * ActiThea Quiz Engine — Glow Flow & Focus Flow
 * --------------------------------------------------------------------------
 * Native Shopify quiz experience (no Typeform). Fully encapsulated: nothing
 * is attached to `window` except two custom element definitions
 * (<actithea-quiz-app> and <actithea-quiz-result-app>), which is the
 * standard, conflict-free way this theme already registers behaviour
 * (see <x-header>, <height-observer>, <cart-dot> in sections/header.liquid).
 *
 * Responsibilities:
 *  - QUIZ_CONFIG / SCORING_CONFIG: question + scoring definitions (NOT
 *    editable from the theme editor on purpose, see mission spec §31/§32).
 *  - calculateScores() / determineProfiles(): pure functions, no side effects.
 *  - saveQuizState() / loadQuizState() / clearQuizState(): sessionStorage
 *    persistence, namespaced, defensive against unavailable storage.
 *  - publishQuizEvent(): dispatches namespaced `actithea:*` DOM CustomEvents
 *    only — never touches Shopify.analytics.publish or any Meta/GTM config.
 *  - renderResult(): renders the result page from sessionStorage state.
 * ========================================================================== */
(function () {
  'use strict';

  if (window.__actitheaQuizEngineLoaded) return;
  window.__actitheaQuizEngineLoaded = true;

  var STORAGE_PREFIX = 'actithea_quiz_';

  /* ------------------------------------------------------------------
   * QUIZ_CONFIG
   * Each question: { id, type: 'single'|'multi', max (for multi),
   *   options: [{ code, label, scores:{DIM: delta, ...} }],
   *   conditional: function(answers) -> boolean (optional, sub-question) }
   * ------------------------------------------------------------------ */
  var QUIZ_CONFIG = {
    glow: {
      dimensions: ['GLOW_ECLAT', 'GLOW_HYDRATATION', 'GLOW_AGE_FERMETE', 'GLOW_GLOBAL', 'GLOW_PLAISIR_REGULARITE'],
      profiles: {
        GLOW_ECLAT: 'eclat',
        GLOW_HYDRATATION: 'hydratation',
        GLOW_AGE_FERMETE: 'age_fermete',
        GLOW_GLOBAL: 'beaute_globale',
        GLOW_PLAISIR_REGULARITE: 'plaisir_regularite'
      },
      tieBreakQuestion: 'q2',
      questions: [
        {
          id: 'q1_age',
          type: 'single',
          scored: false,
          title: 'Quel est votre âge ?',
          options: [
            { code: 'age_moins_30', label: 'Moins de 30 ans' },
            { code: 'age_30_39', label: '30 à 39 ans' },
            { code: 'age_40_49', label: '40 à 49 ans' },
            { code: 'age_50_59', label: '50 à 59 ans' },
            { code: 'age_60_plus', label: '60 ans et plus' }
          ]
        },
        {
          id: 'q2',
          type: 'multi',
          max: 2,
          title: 'Aujourd’hui, qu’aimeriez-vous surtout améliorer au niveau de votre peau ?',
          instruction: 'Choisissez jusqu’à 2 réponses.',
          options: [
            { code: 'eclat', label: 'Retrouver davantage d’éclat', scores: { GLOW_ECLAT: 3 } },
            { code: 'hydratation', label: 'Améliorer l’hydratation et le confort de ma peau', scores: { GLOW_HYDRATATION: 3 } },
            { code: 'ridules', label: 'Agir sur les ridules / premiers signes de l’âge', scores: { GLOW_AGE_FERMETE: 3 } },
            { code: 'fermete', label: 'Améliorer la fermeté / l’élasticité', scores: { GLOW_AGE_FERMETE: 3 } },
            { code: 'preserver', label: 'Préserver la qualité de ma peau dans le temps', scores: { GLOW_GLOBAL: 2 } },
            { code: 'ne_sait_pas', label: 'Je ne sais pas vraiment, j’aimerais justement faire le point', scores: { GLOW_GLOBAL: 1 } }
          ]
        },
        {
          id: 'q3',
          type: 'single',
          title: 'Au-delà de votre peau, avez-vous aussi certaines de ces préoccupations ?',
          options: [
            { code: 'cheveux', label: 'Cheveux ternes ou fragilisés', scores: { GLOW_GLOBAL: 3 } },
            { code: 'ongles', label: 'Ongles fragiles', scores: { GLOW_GLOBAL: 3 } },
            { code: 'les_deux', label: 'Les deux', scores: { GLOW_GLOBAL: 4 } },
            { code: 'non_peau', label: 'Non, ma priorité est surtout ma peau', scores: {} }
          ]
        },
        {
          id: 'q4',
          type: 'single',
          title: 'Avez-vous déjà essayé une cure de collagène ?',
          options: [
            { code: 'regulier', label: 'Oui, et j’en prends encore régulièrement', scores: { GLOW_GLOBAL: 1 } },
            { code: 'difficulte_regularite', label: 'Oui, mais j’ai eu du mal à être régulière', scores: { GLOW_PLAISIR_REGULARITE: 3 } },
            { code: 'gout_texture', label: 'Oui, mais le goût ou la texture m’a découragée', scores: { GLOW_PLAISIR_REGULARITE: 4 } },
            { code: 'experience_peu_agreable', label: 'Oui, mais je n’ai pas vraiment aimé l’expérience', scores: { GLOW_PLAISIR_REGULARITE: 3 } },
            { code: 'jamais', label: 'Non, jamais', scores: {} }
          ],
          subquestion: {
            id: 'q4_sub',
            type: 'single',
            title: 'Qu’est-ce qui vous a le plus gênée ?',
            triggerCodes: ['difficulte_regularite', 'gout_texture', 'experience_peu_agreable'],
            options: [
              { code: 'sub_gout', label: 'Le goût', scores: { GLOW_PLAISIR_REGULARITE: 2 } },
              { code: 'sub_odeur', label: 'L’odeur', scores: { GLOW_PLAISIR_REGULARITE: 2 } },
              { code: 'sub_texture', label: 'La texture', scores: { GLOW_PLAISIR_REGULARITE: 2 } },
              { code: 'sub_melange', label: 'Le fait de devoir le mélanger à une autre boisson', scores: { GLOW_PLAISIR_REGULARITE: 2 } },
              { code: 'sub_tenir_cure', label: 'La difficulté à tenir la cure dans le temps', scores: { GLOW_PLAISIR_REGULARITE: 3 } },
              { code: 'sub_autre', label: 'Autre chose', scores: {} },
              { code: 'sub_rien', label: 'Rien de particulier', scores: {} }
            ]
          }
        },
        {
          id: 'q5',
          type: 'single',
          title: 'Pour qu’une routine beauté fonctionne vraiment dans votre quotidien, qu’est-ce qui compte le plus pour vous ?',
          options: [
            { code: 'gout_agreable', label: 'Un goût agréable que j’ai réellement envie de retrouver chaque jour', scores: { GLOW_PLAISIR_REGULARITE: 3 } },
            { code: 'formule_complete', label: 'Une formule complète avec plusieurs actifs beauté', scores: { GLOW_GLOBAL: 2 } },
            { code: 'simple', label: 'Quelque chose de très simple à intégrer dans ma routine', scores: { GLOW_PLAISIR_REGULARITE: 2 } },
            { code: 'chaud_froid', label: 'Une boisson que je peux boire chaude ou froide', scores: { GLOW_PLAISIR_REGULARITE: 2 } },
            { code: 'premium', label: 'Des ingrédients premium et une formule sans superflu', scores: { GLOW_GLOBAL: 2 } },
            { code: 'tout', label: 'Un peu de tout cela', scores: { GLOW_GLOBAL: 1, GLOW_PLAISIR_REGULARITE: 1 } }
          ]
        }
      ]
    },
    focus: {
      dimensions: ['FOCUS_CHARGE', 'FOCUS_DISPERSION', 'FOCUS_FATIGUE', 'FOCUS_CONCENTRATION', 'FOCUS_PRESSION'],
      profiles: {
        FOCUS_CHARGE: 'charge_mentale',
        FOCUS_DISPERSION: 'energie_dispersee',
        FOCUS_FATIGUE: 'fatigue_mentale',
        FOCUS_CONCENTRATION: 'concentration',
        FOCUS_PRESSION: 'pression_decrochage'
      },
      tieBreakQuestion: 'q3',
      tieBreakQuestion2: 'q5',
      questions: [
        {
          id: 'q1_age',
          type: 'single',
          scored: false,
          title: 'Quel est votre âge ?',
          options: [
            { code: 'age_moins_30', label: 'Moins de 30 ans' },
            { code: 'age_30_39', label: '30 à 39 ans' },
            { code: 'age_40_49', label: '40 à 49 ans' },
            { code: 'age_50_59', label: '50 à 59 ans' },
            { code: 'age_60_plus', label: '60 ans et plus' }
          ]
        },
        {
          id: 'q2',
          type: 'multi',
          max: 2,
          title: 'Dans votre quotidien, qu’est-ce qui vous donne le plus souvent la sensation d’avoir trop de choses en tête ?',
          instruction: 'Choisissez jusqu’à 2 réponses.',
          options: [
            { code: 'travail', label: 'Le travail et les responsabilités professionnelles', scores: { FOCUS_CHARGE: 2 } },
            { code: 'maison', label: 'L’organisation de la maison', scores: { FOCUS_CHARGE: 2 } },
            { code: 'enfants', label: 'Les enfants / la vie familiale', scores: { FOCUS_CHARGE: 2 } },
            { code: 'administratif', label: 'Les rendez-vous, tâches administratives et choses à ne pas oublier', scores: { FOCUS_CHARGE: 2 } },
            { code: 'penser_plusieurs', label: 'Le fait de devoir penser pour plusieurs personnes', scores: { FOCUS_CHARGE: 3 } },
            { code: 'cumuler', label: 'Le fait de cumuler plusieurs de ces choses en même temps', scores: { FOCUS_CHARGE: 4 } },
            { code: 'pas_sensation', label: 'Je n’ai pas particulièrement cette sensation', scores: {} }
          ]
        },
        {
          id: 'q3',
          type: 'multi',
          max: 2,
          title: 'Quand tout s’accumule, comment cela se manifeste-t-il le plus souvent chez vous ?',
          instruction: 'Choisissez jusqu’à 2 réponses.',
          options: [
            { code: 'saturee', label: 'Je me sens mentalement saturée', scores: { FOCUS_CHARGE: 3 } },
            { code: 'dispersee', label: 'Je deviens facilement dispersée', scores: { FOCUS_DISPERSION: 3 } },
            { code: 'concentration', label: 'J’ai du mal à rester concentrée', scores: { FOCUS_CONCENTRATION: 3 } },
            { code: 'manque_energie', label: 'Je manque d’énergie pour avancer', scores: { FOCUS_FATIGUE: 3 } },
            { code: 'repousse', label: 'Je repousse certaines tâches parce que je ne sais plus par où commencer', scores: { FOCUS_DISPERSION: 2, FOCUS_CONCENTRATION: 1 } },
            { code: 'sous_pression', label: 'Je me sens sous pression ou stressée', scores: { FOCUS_PRESSION: 3 } },
            { code: 'decrochage', label: 'J’ai du mal à décrocher même lorsque ma journée est terminée', scores: { FOCUS_PRESSION: 3 } }
          ]
        },
        {
          id: 'q4',
          type: 'single',
          title: 'Quand vous avez enfin un moment pour avancer sur quelque chose d’important pour vous…',
          options: [
            { code: 'trop_fatiguee', label: 'Je suis souvent déjà trop fatiguée mentalement', scores: { FOCUS_FATIGUE: 3 } },
            { code: 'temps_concentrer', label: 'Je mets du temps à réussir à me concentrer', scores: { FOCUS_CONCENTRATION: 3 } },
            { code: 'interrompue', label: 'Je commence, puis je suis rapidement interrompue', scores: { FOCUS_DISPERSION: 2 } },
            { code: 'plusieurs_directions', label: 'Mon esprit part dans plusieurs directions à la fois', scores: { FOCUS_DISPERSION: 3 } },
            { code: 'mal_action', label: 'J’ai du mal à me mettre en action', scores: { FOCUS_FATIGUE: 1, FOCUS_CONCENTRATION: 2 } },
            { code: 'avance_correctement', label: 'J’avance plutôt correctement', scores: {} },
            { code: 'depend', label: 'Cela dépend énormément des journées', scores: { FOCUS_DISPERSION: 1, FOCUS_FATIGUE: 1 } }
          ]
        },
        {
          id: 'q5',
          type: 'single',
          title: 'Si vous pouviez changer une chose dans vos journées, laquelle choisiriez-vous ?',
          options: [
            { code: 'clarte', label: 'Retrouver plus de clarté mentale', scores: { FOCUS_CHARGE: 2 } },
            { code: 'concentration', label: 'Réussir à me concentrer plus facilement', scores: { FOCUS_CONCENTRATION: 3 } },
            { code: 'energie', label: 'Avoir davantage d’énergie sans me sentir surexcitée', scores: { FOCUS_FATIGUE: 3 } },
            { code: 'moins_dispersee', label: 'Me sentir moins dispersée', scores: { FOCUS_DISPERSION: 3 } },
            { code: 'efficace', label: 'Être plus efficace quand tout s’accumule', scores: { FOCUS_CHARGE: 2, FOCUS_CONCENTRATION: 1 } },
            { code: 'sereine', label: 'Me sentir plus sereine face aux journées chargées', scores: { FOCUS_PRESSION: 3 } },
            { code: 'tout', label: 'Un peu de tout cela', scores: { FOCUS_CHARGE: 1, FOCUS_DISPERSION: 1, FOCUS_FATIGUE: 1, FOCUS_CONCENTRATION: 1, FOCUS_PRESSION: 1 } }
          ]
        }
      ]
    }
  };

  /* ------------------------------------------------------------------
   * Pure helpers
   * ------------------------------------------------------------------ */

  function calculateScores(quizType, answers) {
    var config = QUIZ_CONFIG[quizType];
    var scores = {};
    config.dimensions.forEach(function (d) { scores[d] = 0; });

    config.questions.forEach(function (q) {
      if (q.scored === false) return;
      applyAnswerScores(q, answers[q.id], scores);
      if (q.subquestion) {
        applyAnswerScores(q.subquestion, answers[q.subquestion.id], scores);
      }
    });

    return scores;
  }

  function applyAnswerScores(question, answerCodes, scores) {
    if (!answerCodes) return;
    var codes = Array.isArray(answerCodes) ? answerCodes : [answerCodes];
    codes.forEach(function (code) {
      var opt = question.options.filter(function (o) { return o.code === code; })[0];
      if (!opt || !opt.scores) return;
      Object.keys(opt.scores).forEach(function (dim) {
        scores[dim] = (scores[dim] || 0) + opt.scores[dim];
      });
    });
  }

  /* Returns { primary: 'eclat', secondary: 'hydratation'|null, scores } */
  function determineProfiles(quizType, scores, answers) {
    var config = QUIZ_CONFIG[quizType];
    var dims = config.dimensions.slice();

    // Deterministic fallback order (documented, never random).
    var fallbackOrder = dims.slice();

    var ranked = dims.slice().sort(function (a, b) {
      if (scores[b] !== scores[a]) return scores[b] - scores[a];
      return fallbackOrder.indexOf(a) - fallbackOrder.indexOf(b);
    });

    var topScore = scores[ranked[0]];
    var tiedTop = dims.filter(function (d) { return scores[d] === topScore; });

    var primaryDim = ranked[0];

    if (tiedTop.length > 1) {
      primaryDim = resolveTie(quizType, tiedTop, answers, fallbackOrder);
    }

    // Re-rank excluding the chosen primary to find the secondary.
    var remaining = dims.filter(function (d) { return d !== primaryDim; });
    remaining.sort(function (a, b) {
      if (scores[b] !== scores[a]) return scores[b] - scores[a];
      return fallbackOrder.indexOf(a) - fallbackOrder.indexOf(b);
    });

    var secondaryDim = remaining[0];
    var secondaryScore = scores[secondaryDim];
    var primaryScore = scores[primaryDim];

    // Hide secondary profile if score is 0, or not meaningfully significant
    // compared to the primary (below 40% of the primary score).
    var significant = secondaryScore > 0 && primaryScore > 0 && (secondaryScore / primaryScore) >= 0.4;

    return {
      primary: config.profiles[primaryDim],
      secondary: significant ? config.profiles[secondaryDim] : null,
      scores: scores
    };
  }

  function resolveTie(quizType, tiedDims, answers, fallbackOrder) {
    var config = QUIZ_CONFIG[quizType];
    var tieQuestions = [config.tieBreakQuestion, config.tieBreakQuestion2].filter(Boolean);

    for (var t = 0; t < tieQuestions.length; t++) {
      var qId = tieQuestions[t];
      var question = config.questions.filter(function (q) { return q.id === qId; })[0];
      if (!question) continue;
      var answerCodes = answers[qId];
      if (!answerCodes) continue;
      var codes = Array.isArray(answerCodes) ? answerCodes : [answerCodes];

      for (var c = 0; c < codes.length; c++) {
        var opt = question.options.filter(function (o) { return o.code === codes[c]; })[0];
        if (!opt || !opt.scores) continue;
        var optDims = Object.keys(opt.scores);
        for (var o = 0; o < optDims.length; o++) {
          if (tiedDims.indexOf(optDims[o]) !== -1) return optDims[o];
        }
      }
    }

    // Ultimate deterministic fallback — never random.
    var sortedTied = tiedDims.slice().sort(function (a, b) {
      return fallbackOrder.indexOf(a) - fallbackOrder.indexOf(b);
    });
    return sortedTied[0];
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  // Defense-in-depth sanitizer: strips any HTML tags and control chars from
  // free-text user input before it is ever stored or displayed. Display code
  // additionally always uses textContent, never innerHTML, for user data.
  function sanitizeText(value, maxLength) {
    var str = String(value == null ? '' : value);
    str = str.replace(/<[^>]*>/g, '');
    str = str.split('').filter(function (ch) { var c = ch.charCodeAt(0); return c > 31 && c !== 127; }).join('');
    str = str.trim();
    if (maxLength && str.length > maxLength) str = str.slice(0, maxLength);
    return str;
  }

  function storageAvailable() {
    try {
      var k = '__actithea_test__';
      window.sessionStorage.setItem(k, '1');
      window.sessionStorage.removeItem(k);
      return true;
    } catch (e) {
      return false;
    }
  }

  function saveQuizState(quizType, state) {
    if (!storageAvailable()) return false;
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + quizType, JSON.stringify(state));
      return true;
    } catch (e) {
      return false;
    }
  }

  function loadQuizState(quizType) {
    if (!storageAvailable()) return null;
    try {
      var raw = window.sessionStorage.getItem(STORAGE_PREFIX + quizType);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function clearQuizState(quizType) {
    if (!storageAvailable()) return;
    try {
      window.sessionStorage.removeItem(STORAGE_PREFIX + quizType);
    } catch (e) { /* no-op */ }
  }

  function publishQuizEvent(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true }));
    } catch (e) { /* no-op: never break the quiz because of analytics */ }
  }

  // Publishes `actithea:marketing_lead` — but only the first time this is
  // called for a given opt-in. Always reloads the latest sessionStorage
  // quiz state (rather than trusting an in-memory reference) so a flag
  // already set is respected and the event is never published twice for
  // the same opt-in. Carries no email, first name, age, or answer data —
  // only the quiz type, per spec. Called from <actithea-quiz-result-app>
  // once it sees `state.optinConfirmed === true` (see
  // ResultApp.prototype.renderResult below) — set only once the external
  // backend (see QuizApp.prototype.submitOptIn) has confirmed with
  // { success: true } that the Shopify customer was actually
  // created/updated with marketing consent.
  function maybeSendMarketingLead(quizType) {
    var latest = loadQuizState(quizType);
    if (!latest || latest.marketingLeadEventSent) return;
    latest.marketingLeadEventSent = true;
    saveQuizState(quizType, latest);

    publishQuizEvent('actithea:marketing_lead', { quiz_type: quizType });

    // Shopify custom pixels (Customer Events) run in an isolated sandbox
    // with no access to this page's document, so the DOM CustomEvent
    // above never reaches them. Shopify.analytics.publish() is the
    // documented, sanctioned bridge for exactly this case: a custom pixel
    // that calls analytics.subscribe('actithea:marketing_lead', ...)
    // receives this same payload via event.customData. This only reaches
    // a custom pixel that explicitly subscribes to this exact custom
    // event name — it never touches or retriggers any of Shopify's own
    // standard commerce events (page_viewed, product_added_to_cart,
    // checkout_completed, etc.), so it cannot duplicate anything already
    // sent by the official Facebook & Instagram by Meta app.
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('actithea:marketing_lead', { quiz_type: quizType });
      }
    } catch (e) { /* no-op */ }
  }

  var prefersReducedMotion = false;
  try {
    prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* ignore */ }

  /* ------------------------------------------------------------------
   * <actithea-quiz-app>
   * ------------------------------------------------------------------ */
  function defineQuizApp() {
    function QuizApp() {
      return Reflect.construct(HTMLElement, [], QuizApp);
    }
    QuizApp.prototype = Object.create(HTMLElement.prototype);
    QuizApp.prototype.constructor = QuizApp;

    QuizApp.prototype.connectedCallback = function () {
      if (this.__initialized) return;
      this.__initialized = true;

      var quizType = this.dataset.quiz;
      var config = QUIZ_CONFIG[quizType];
      if (!config) return;

      this.quizType = quizType;
      this.config = config;
      // All top-level questions are shown to the visitor (including the
      // unscored age question, q1_age) — only calculateScores() filters by
      // `scored`. Display order must never be conflated with scoring.
      this.displayQuestions = config.questions.slice();
      this.answers = {};
      this.currentIndex = -1; // -1 = intro
      this.activeQuestion = null; // current question object (main or sub)
      this.flatSequence = []; // built dynamically as we know which subquestions apply

      this.els = {
        progress: this.querySelector('[data-aq-progress]'),
        progressFill: this.querySelector('[data-aq-progress-fill]'),
        progressLabel: this.querySelector('[data-aq-progress-label]'),
        screenIntro: this.querySelector('[data-screen="intro"]'),
        screenQuestion: this.querySelector('[data-screen="question"]'),
        screenLead: this.querySelector('[data-screen="lead"]'),
        introCta: this.querySelector('[data-aq-intro-cta]'),
        instruction: this.querySelector('[data-aq-instruction]'),
        questionTitle: this.querySelector('[data-aq-question-title]'),
        choices: this.querySelector('[data-aq-choices]'),
        continueBtn: this.querySelector('[data-aq-continue]'),
        backBtn: this.querySelector('[data-aq-back]'),
        leadForm: this.querySelector('[data-screen="lead"] form'),
        firstNameInput: this.querySelector('[data-aq-firstname]'),
        emailInput: this.querySelector('[data-aq-email]'),
        firstNameError: this.querySelector('[data-aq-firstname-error]'),
        emailError: this.querySelector('[data-aq-email-error]'),
        consentInput: this.querySelector('[data-aq-consent]'),
        leadSubmit: this.querySelector('[data-aq-lead-submit]'),
        leadBack: this.querySelector('[data-aq-lead-back]'),
        submitError: this.querySelector('[data-aq-submit-error]')
      };

      // Plain form (not a Shopify `{% form 'customer' %}`) posted via
      // fetch() to an external backend — see handleLeadSubmit below.
      this.backendUrl = this.dataset.backendUrl || '';

      this.bindEvents();
      this.resumeOrStart();
    };

    QuizApp.prototype.bindEvents = function () {
      var self = this;

      if (this.els.introCta) {
        this.els.introCta.addEventListener('click', function () {
          clearQuizState(self.quizType);
          self.answers = {};
          publishQuizEvent('actithea:quiz_started', { quiz_type: self.quizType });
          self.goToQuestion(0);
        });
      }

      if (this.els.continueBtn) {
        this.els.continueBtn.addEventListener('click', function () {
          self.handleContinue();
        });
      }

      if (this.els.backBtn) {
        this.els.backBtn.addEventListener('click', function () {
          self.handleBack();
        });
      }

      if (this.els.leadBack) {
        this.els.leadBack.addEventListener('click', function () {
          self.showScreen('question');
          self.renderQuestion();
        });
      }

      if (this.els.leadForm) {
        // Plain form, always prevented: handleLeadSubmit() drives the
        // whole flow itself (validation, then either an immediate
        // redirect with no network call when consent is unchecked, or a
        // fetch() to the external backend when it is checked).
        this.els.leadForm.addEventListener('submit', function (evt) {
          self.handleLeadSubmit(evt);
        });
      }

      if (this.els.emailInput) {
        this.els.emailInput.addEventListener('input', function () {
          self.clearFieldError('email');
        });
      }
      if (this.els.firstNameInput) {
        this.els.firstNameInput.addEventListener('input', function () {
          self.clearFieldError('firstname');
        });
      }
    };

    QuizApp.prototype.resumeOrStart = function () {
      var saved = loadQuizState(this.quizType);
      if (saved && saved.answers && !saved.completed && typeof saved.currentIndex === 'number' && saved.currentIndex >= 0) {
        this.answers = saved.answers;
        this.showScreen('question');
        this.goToQuestion(saved.currentIndex, true);
      }
      // Otherwise stay on intro screen (default active screen server-rendered).
    };

    QuizApp.prototype.persist = function (completed) {
      saveQuizState(this.quizType, {
        answers: this.answers,
        currentIndex: this.currentIndex,
        completed: !!completed
      });
    };

    QuizApp.prototype.totalDisplayQuestions = function () {
      return this.displayQuestions.length;
    };

    QuizApp.prototype.goToQuestion = function (index, silent) {
      if (index >= this.displayQuestions.length) {
        this.showLeadStep();
        return;
      }
      this.currentIndex = index;
      this.activeQuestion = this.displayQuestions[index];
      this.activeIsSub = false;
      this.showScreen('question');
      this.renderQuestion();
      if (!silent) this.persist(false);
    };

    QuizApp.prototype.renderQuestion = function () {
      var q = this.activeQuestion;
      var self = this;

      // Progress reflects the main (visible) question count, sub-question
      // included within the same step per mission spec §27.
      if (this.els.progress) this.els.progress.hidden = false;
      var stepNumber = this.currentIndex + 1;
      var total = this.totalDisplayQuestions();
      if (this.els.progressLabel) {
        this.els.progressLabel.textContent = stepNumber + ' / ' + total;
      }
      if (this.els.progressFill) {
        this.els.progressFill.style.width = Math.round((stepNumber / total) * 100) + '%';
      }

      if (this.els.instruction) {
        if (q.instruction) {
          this.els.instruction.textContent = q.instruction;
          this.els.instruction.hidden = false;
        } else {
          this.els.instruction.hidden = true;
        }
      }

      if (this.els.questionTitle) this.els.questionTitle.textContent = q.title;

      this.els.choices.innerHTML = '';
      this.selected = Array.isArray(this.answers[q.id]) ? this.answers[q.id].slice() : (this.answers[q.id] ? [this.answers[q.id]] : []);

      q.options.forEach(function (opt) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'aq__choice';
        btn.dataset.code = opt.code;
        var isMulti = q.type === 'multi';
        btn.setAttribute('role', isMulti ? 'checkbox' : 'radio');
        btn.dataset.inputType = isMulti ? 'checkbox' : 'radio';
        var checked = self.selected.indexOf(opt.code) !== -1;
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');

        var mark = document.createElement('span');
        mark.className = 'aq__choice-mark';
        mark.setAttribute('aria-hidden', 'true');
        mark.innerHTML = '<svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5L4.5 8.5L11 1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        mark.style.color = '#fff';

        var label = document.createElement('span');
        label.className = 'aq__choice-label';
        label.textContent = opt.label;

        btn.appendChild(mark);
        btn.appendChild(label);
        btn.addEventListener('click', function () { self.handleChoice(opt.code); });
        self.els.choices.appendChild(btn);
      });

      this.updateContinueState();
      this.els.backBtn.hidden = this.currentIndex === 0 && !this.activeIsSub;
      this.playTransition(this.els.screenQuestion);
    };

    QuizApp.prototype.handleChoice = function (code) {
      var q = this.activeQuestion;

      if (q.type === 'multi') {
        var idx = this.selected.indexOf(code);
        if (idx !== -1) {
          this.selected.splice(idx, 1);
        } else {
          if (this.selected.length >= (q.max || 2)) return; // cap reached
          this.selected.push(code);
        }
        this.answers[q.id] = this.selected.slice();
        this.syncChoiceUI();
        this.updateContinueState();
      } else {
        this.selected = [code];
        this.answers[q.id] = code;
        this.syncChoiceUI();
        this.persist(false);

        // Single-select auto-advance with a short, pleasant transition.
        var self = this;
        var delay = prefersReducedMotion ? 80 : 260;
        this.els.choices.querySelectorAll('.aq__choice').forEach(function (b) { b.disabled = true; });
        setTimeout(function () {
          self.els.choices.querySelectorAll('.aq__choice').forEach(function (b) { b.disabled = false; });
          self.advance();
        }, delay);
      }
    };

    QuizApp.prototype.syncChoiceUI = function () {
      var self = this;
      this.els.choices.querySelectorAll('.aq__choice').forEach(function (btn) {
        var checked = self.selected.indexOf(btn.dataset.code) !== -1;
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');
      });
    };

    QuizApp.prototype.updateContinueState = function () {
      var q = this.activeQuestion;
      var hasAnswer = this.selected && this.selected.length > 0;
      if (q.type === 'multi') {
        this.els.continueBtn.hidden = false;
        this.els.continueBtn.disabled = !hasAnswer;
      } else {
        // Single-select advances automatically; keep Continue hidden.
        this.els.continueBtn.hidden = true;
      }
    };

    QuizApp.prototype.handleContinue = function () {
      if (!this.selected || !this.selected.length) return;
      this.advance();
    };

    QuizApp.prototype.advance = function () {
      var q = this.activeQuestion;

      // Check for a conditional sub-question (Glow Q4).
      if (!this.activeIsSub && q.subquestion) {
        var answerCode = this.answers[q.id];
        var triggers = q.subquestion.triggerCodes || [];
        if (triggers.indexOf(answerCode) !== -1) {
          this.activeIsSub = true;
          this.activeQuestion = q.subquestion;
          this.selected = Array.isArray(this.answers[q.subquestion.id]) ? this.answers[q.subquestion.id].slice() : (this.answers[q.subquestion.id] ? [this.answers[q.subquestion.id]] : []);
          this.renderSubQuestion();
          this.persist(false);
          return;
        }
      }

      this.activeIsSub = false;
      this.goToQuestion(this.currentIndex + 1);
    };

    QuizApp.prototype.renderSubQuestion = function () {
      // Sub-question reuses the same screen/progress (does not increment
      // the visible step counter — mission spec §27).
      var q = this.activeQuestion;
      var self = this;

      if (this.els.instruction) this.els.instruction.hidden = true;
      if (this.els.questionTitle) this.els.questionTitle.textContent = q.title;

      this.els.choices.innerHTML = '';
      q.options.forEach(function (opt) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'aq__choice';
        btn.dataset.code = opt.code;
        btn.setAttribute('role', 'radio');
        btn.dataset.inputType = 'radio';
        var checked = self.selected.indexOf(opt.code) !== -1;
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');

        var mark = document.createElement('span');
        mark.className = 'aq__choice-mark';
        mark.setAttribute('aria-hidden', 'true');
        mark.innerHTML = '<svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 5L4.5 8.5L11 1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        var label = document.createElement('span');
        label.className = 'aq__choice-label';
        label.textContent = opt.label;

        btn.appendChild(mark);
        btn.appendChild(label);
        btn.addEventListener('click', function () {
          self.selected = [opt.code];
          self.answers[q.id] = opt.code;
          self.syncChoiceUI();
          self.persist(false);
          var delay = prefersReducedMotion ? 80 : 260;
          self.els.choices.querySelectorAll('.aq__choice').forEach(function (b) { b.disabled = true; });
          setTimeout(function () {
            self.els.choices.querySelectorAll('.aq__choice').forEach(function (b) { b.disabled = false; });
            self.activeIsSub = false;
            self.goToQuestion(self.currentIndex + 1);
          }, delay);
        });
        self.els.choices.appendChild(btn);
      });

      this.els.continueBtn.hidden = true;
      this.els.backBtn.hidden = false;
      this.playTransition(this.els.screenQuestion);
    };

    QuizApp.prototype.handleBack = function () {
      if (this.activeIsSub) {
        // Go back from sub-question to the parent question.
        var q = this.displayQuestions[this.currentIndex];
        this.activeIsSub = false;
        this.activeQuestion = q;
        this.selected = Array.isArray(this.answers[q.id]) ? this.answers[q.id].slice() : (this.answers[q.id] ? [this.answers[q.id]] : []);
        this.renderQuestion();
        return;
      }
      if (this.currentIndex <= 0) return;
      this.goToQuestion(this.currentIndex - 1, true);
    };

    QuizApp.prototype.showLeadStep = function () {
      this.persist(false);
      this.showScreen('lead');
      this.playTransition(this.els.screenLead);
    };

    QuizApp.prototype.clearFieldError = function (which) {
      if (which === 'email' && this.els.emailError) {
        this.els.emailError.textContent = '';
        this.els.emailInput.setAttribute('aria-invalid', 'false');
      }
      if (which === 'firstname' && this.els.firstNameError) {
        this.els.firstNameError.textContent = '';
        this.els.firstNameInput.setAttribute('aria-invalid', 'false');
      }
    };

    // Plain form 'submit' event — always prevented. This handler owns the
    // whole flow: validation, then either an immediate redirect (no
    // network call at all) when marketing consent is unchecked, or a
    // fetch() to the external backend (Cloudflare Worker) when it is
    // checked. That backend — not this code — holds the Shopify Admin API
    // token and actually creates/updates the customer.
    QuizApp.prototype.handleLeadSubmit = function (evt) {
      if (evt) evt.preventDefault();

      var firstNameRaw = this.els.firstNameInput ? this.els.firstNameInput.value : '';
      var emailRaw = this.els.emailInput ? this.els.emailInput.value : '';
      var firstName = sanitizeText(firstNameRaw, 60);
      var email = sanitizeText(emailRaw, 190);
      var consent = this.els.consentInput ? this.els.consentInput.checked : false;

      var hasError = false;
      if (!firstName) {
        if (this.els.firstNameError) this.els.firstNameError.textContent = 'Merci d’indiquer votre prénom.';
        if (this.els.firstNameInput) this.els.firstNameInput.setAttribute('aria-invalid', 'true');
        hasError = true;
      }
      if (!email || !isValidEmail(email)) {
        if (this.els.emailError) this.els.emailError.textContent = 'Merci d’indiquer une adresse email valide.';
        if (this.els.emailInput) this.els.emailInput.setAttribute('aria-invalid', 'true');
        hasError = true;
      }
      if (hasError) {
        return;
      }

      var scores = calculateScores(this.quizType, this.answers);
      var result = determineProfiles(this.quizType, scores, this.answers);

      var state = {
        quiz: this.quizType,
        answers: this.answers,
        scores: scores,
        primaryProfile: result.primary,
        secondaryProfile: result.secondary,
        firstName: firstName,
        completedAt: new Date().toISOString(),
        completed: true,
        marketingLeadEventSent: false
      };

      saveQuizState(this.quizType, state);

      publishQuizEvent('actithea:quiz_completed', {
        quiz_type: this.quizType,
        primary_profile: result.primary,
        secondary_profile: result.secondary
      });

      var resultUrl = this.dataset.resultUrl;

      if (!consent) {
        // No marketing consent: never call the backend, never register
        // anything anywhere. Straight to the result, exactly as before.
        if (resultUrl) window.location.href = resultUrl;
        return;
      }

      this.submitOptIn(firstName, email, resultUrl, result.primary, result.secondary);
    };

    // Sends { firstName, email, marketingConsent: true, quizType } to the
    // external backend (Cloudflare Worker), which is the only place that
    // ever holds a Shopify Admin API token — never this file, never any
    // theme file. The backend creates/updates the real Shopify customer,
    // sets emailMarketingConsent and the quiz tag, and replies with
    // { success: true } only once that has actually happened. Only on
    // that confirmed reply do we mark this opt-in as confirmed in
    // sessionStorage; <actithea-quiz-result-app> reads that flag (not a
    // URL param — there is no Shopify redirect anymore) and fires the
    // Meta "Lead" event from there, still never at the click and never
    // before the server confirms. On any failure, the visitor still
    // reaches their result (never block the quiz on a network hiccup);
    // no Lead is fired and the opt-in is simply left unconfirmed.
    QuizApp.prototype.submitOptIn = function (firstName, email, resultUrl, primaryProfile, secondaryProfile) {
      var self = this;

      if (this.els.submitError) this.els.submitError.textContent = '';

      if (!this.backendUrl) {
        // Not configured yet in the theme editor: fail safe, don't block
        // the visitor's result, but make it obvious in the console for
        // whoever is setting this up.
        console.error('ActiThea quiz: no backend URL configured (section setting "backend_url").');
        if (resultUrl) window.location.href = resultUrl;
        return;
      }

      if (this.els.leadSubmit) {
        this.els.leadSubmit.disabled = true;
        this.els.leadSubmit.textContent = this.els.leadSubmit.dataset.loadingText || '...';
      }

      fetch(this.backendUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName,
          email: email,
          marketingConsent: true,
          quizType: this.quizType,
          primaryProfile: primaryProfile || null,
          secondaryProfile: secondaryProfile || null
        })
      })
        .then(function (res) { return res.json().catch(function () { return {}; }); })
        .then(function (data) {
          if (data && data.success === true) {
            var latest = loadQuizState(self.quizType) || {};
            latest.optinConfirmed = true;
            saveQuizState(self.quizType, latest);
          } else {
            console.error('ActiThea quiz: backend did not confirm opt-in', data && data.error);
          }
          if (resultUrl) window.location.href = resultUrl;
        })
        .catch(function (err) {
          console.error('ActiThea quiz: backend request failed', err);
          // Still let the visitor see their result — never lose the
          // conversion over a network error. No Lead fires since
          // optinConfirmed is never set on this path.
          if (resultUrl) window.location.href = resultUrl;
        });
    };

    QuizApp.prototype.showScreen = function (name) {
      var screens = {
        intro: this.els.screenIntro,
        question: this.els.screenQuestion,
        lead: this.els.screenLead
      };
      Object.keys(screens).forEach(function (key) {
        var el = screens[key];
        if (!el) return;
        el.classList.toggle('is-active', key === name);
      });
      if (this.els.progress) this.els.progress.hidden = name !== 'question';
    };

    QuizApp.prototype.playTransition = function (el) {
      if (!el || prefersReducedMotion) return;
      el.classList.remove('is-entering');
      // Force reflow so the animation can restart.
      void el.offsetWidth;
      el.classList.add('is-entering');
    };

    return QuizApp;
  }

  if (!customElements.get('actithea-quiz-app')) {
    customElements.define('actithea-quiz-app', defineQuizApp());
  }

  /* ------------------------------------------------------------------
   * <actithea-quiz-result-app>
   * ------------------------------------------------------------------ */
  function defineResultApp() {
    function ResultApp() {
      return Reflect.construct(HTMLElement, [], ResultApp);
    }
    ResultApp.prototype = Object.create(HTMLElement.prototype);
    ResultApp.prototype.constructor = ResultApp;

    ResultApp.prototype.connectedCallback = function () {
      if (this.__initialized) return;
      this.__initialized = true;

      var quizType = this.dataset.quiz;
      var config = QUIZ_CONFIG[quizType];
      if (!config) return;
      this.quizType = quizType;
      this.config = config;

      this.els = {
        empty: this.querySelector('[data-aqr-empty]'),
        content: this.querySelector('[data-aqr-content]'),
        firstNameSlots: this.querySelectorAll('[data-aqr-firstname]'),
        primaryBlocks: this.querySelectorAll('[data-aqr-profile]'),
        secondaryWrap: this.querySelector('[data-aqr-secondary]'),
        secondaryLabel: this.querySelector('[data-aqr-secondary-label]'),
        answerNotes: this.querySelectorAll('[data-aqr-if-answer]'),
        productClickTargets: this.querySelectorAll('[data-aqr-product-link]')
      };

      this.renderResult();
    };

    ResultApp.prototype.renderResult = function () {
      var state = loadQuizState(this.quizType);
      var valid = state && state.completed && state.primaryProfile;

      if (!valid) {
        if (this.els.empty) this.els.empty.hidden = false;
        if (this.els.content) this.els.content.hidden = true;
        return;
      }

      // `state.optinConfirmed` is set (see
      // QuizApp.prototype.submitOptIn in this file) only once the external
      // backend has replied { success: true } — i.e. only once the
      // Shopify customer has actually been created/updated with marketing
      // consent and the quiz tag. The Meta "Lead" event fires here, once,
      // from that confirmed state — never at the click, never before the
      // server confirms.
      try {
        if (state.optinConfirmed) {
          maybeSendMarketingLead(this.quizType);
        }
      } catch (e) { /* no-op */ }

      if (this.els.empty) this.els.empty.hidden = true;
      if (this.els.content) this.els.content.hidden = false;

      var firstName = sanitizeText(state.firstName, 60) || '';
      this.els.firstNameSlots.forEach(function (el) {
        el.textContent = firstName;
      });

      // Show only the primary profile block, hide every other profile block.
      this.els.primaryBlocks.forEach(function (el) {
        var isPrimary = el.getAttribute('data-aqr-profile') === state.primaryProfile;
        el.hidden = !isPrimary;
      });

      // Secondary profile mini-block.
      if (this.els.secondaryWrap) {
        if (state.secondaryProfile) {
          this.els.secondaryWrap.hidden = false;
          this.els.secondaryWrap.querySelectorAll('[data-aqr-secondary-profile]').forEach(function (el) {
            el.hidden = el.getAttribute('data-aqr-secondary-profile') !== state.secondaryProfile;
          });
        } else {
          this.els.secondaryWrap.hidden = true;
        }
      }

      // Reveal small "based on your real answer" sentences — only ever the
      // ones matching an answer the visitor actually gave (mission spec §10).
      var flatAnswers = [];
      Object.keys(state.answers || {}).forEach(function (key) {
        var val = state.answers[key];
        if (Array.isArray(val)) flatAnswers = flatAnswers.concat(val);
        else if (val) flatAnswers.push(val);
      });

      this.els.answerNotes.forEach(function (el) {
        var requiredCode = el.getAttribute('data-aqr-if-answer');
        el.hidden = flatAnswers.indexOf(requiredCode) === -1;
      });

      publishQuizEvent('actithea:quiz_result_viewed', {
        quiz_type: this.quizType,
        primary_profile: state.primaryProfile
      });

      var handle = this.dataset.productHandle || '';
      this.els.productClickTargets.forEach(function (el) {
        el.addEventListener('click', function () {
          publishQuizEvent('actithea:quiz_product_clicked', {
            quiz_type: state.quiz,
            product_handle: handle
          });
        });
      });
    };

    return ResultApp;
  }

  if (!customElements.get('actithea-quiz-result-app')) {
    customElements.define('actithea-quiz-result-app', defineResultApp());
  }
})();
